import type { CallFlowSummary, FlowTemplate, Me } from '@smarton-pbx/shared';
import { callFlowSchema } from '@smarton-pbx/shared';
import { MoreHorizontal, Pencil, Plus, Trash2, Workflow } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { api } from '../../api';
import { locale, t, tc } from '../../i18n';
import { Dialog, EmptyState, Field, Loading, Menu, Notice, opensOnClick, PageHeader, Pill } from '../../components/ui';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../../components/table';
import { formatNumber } from '../../lib/calls';
import { useLoad, useSubmit } from '../../lib/hooks';
import { SectionTabs } from './SectionTabs';

const newFlowSchema = callFlowSchema.omit({ graph: true }).extend({ template: z.string() });

/** Admins and region managers: the call flows, which numbers start each, and what is live. */
/** Which version calls get, in words. */
const liveText = (flow: CallFlowSummary) =>
  flow.published
    ? flow.changed
      ? t('Version {n} · draft changed', { n: flow.published.number })
      : t('Version {n}', { n: flow.published.number })
    : t('Not published');

function flowColumns(): Column<CallFlowSummary>[] {
  return [
    {
      key: 'flow',
      label: t('Flow'),
      fixed: true,
      cell: (flow) => (
        <>
          <Link to={`/flows/${flow.id}`} className="name">
            {flow.name}
          </Link>
          <div className="detail subtle">
            {t('{region} · changed {date}', { region: flow.regionName, date: new Date(flow.updatedAt).toLocaleDateString(locale) })}
          </div>
        </>
      ),
      excel: [
        { label: t('Flow'), value: (flow) => flow.name },
        { label: t('Region'), value: (flow) => flow.regionName },
        { label: t('Changed'), value: (flow) => new Date(flow.updatedAt) },
      ],
    },
    {
      key: 'live',
      label: tc('version', 'Live'),
      cell: (flow) =>
        flow.published ? (
          <Pill kind={flow.changed ? 'warning' : 'success'} dot={!flow.changed}>
            {liveText(flow)}
          </Pill>
        ) : (
          <Pill kind="warning">{liveText(flow)}</Pill>
        ),
      value: liveText,
    },
    {
      key: 'numbers',
      label: t('Numbers'),
      className: 'hide-mobile muted tabular',
      cell: (flow) => flow.numbers.map((n) => formatNumber(n.e164)).join(', ') || '—',
      value: (flow) => flow.numbers.map((n) => n.e164).join(', '),
    },
  ];
}

export function FlowsPage({ me }: { me: Me }) {
  const flows = useLoad(api.listFlows);
  const navigate = useNavigate();
  const table = useTable('call-flows', flowColumns(), mayDownload(me.role) ? { name: t('Call flows'), rows: () => flows.data ?? [] } : null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();

  async function remove(id: string, name: string) {
    if (!confirm(t('Delete the flow {name}?', { name }))) return;
    try {
      await api.deleteFlow(id);
      flows.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="stack">
      <SectionTabs />
      <PageHeader title={t('Call flows')} description={t('What happens to a call to your numbers: menus, opening hours, queues, voicemail. Draw it, test it, publish it.')}>
        <button type="button" onClick={() => setCreating(true)} data-testid="add-flow">
          <Plus size={16} aria-hidden /> {t('New flow')}
        </button>
      </PageHeader>
      {(error ?? flows.error) && <Notice kind="error">{error ?? flows.error}</Notice>}
      {!flows.data ? (
        <Loading />
      ) : flows.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={Workflow} title={t('No call flows yet')}>
            <p>{t('Start from a template such as “menu, opening hours, queue, voicemail”, then point a number at it.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {flows.data.map((flow) => (
                <tr key={flow.id} data-testid="flow-row" {...opensOnClick(() => void navigate(`/flows/${flow.id}`))}>
                  <Cells table={table} row={flow} />
                  <td className="actions">
                    <Menu
                      label={t('Actions for {name}', { name: flow.name })}
                      trigger={(props) => (
                        <button type="button" className="ghost icon" aria-label={t('Actions for {name}', { name: flow.name })} {...props}>
                          <MoreHorizontal size={18} aria-hidden />
                        </button>
                      )}
                    >
                      {(close) => (
                        <>
                          <Link to={`/flows/${flow.id}`} role="menuitem" onClick={close}>
                            <Pencil size={16} aria-hidden /> {tc('action', 'Open')}
                          </Link>
                          <button type="button" role="menuitem" className="danger-item" onClick={() => (close(), void remove(flow.id, flow.name))}>
                            <Trash2 size={16} aria-hidden /> {t('Delete')}
                          </button>
                        </>
                      )}
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={creating} onClose={() => setCreating(false)} title={t('New call flow')} wide>
        {creating && <NewFlowForm me={me} onCancel={() => setCreating(false)} />}
      </Dialog>
    </div>
  );
}

function NewFlowForm({ me, onCancel }: { me: Me; onCancel: () => void }) {
  const options = useLoad(() => Promise.all([api.listRegions(), api.flowTemplates()]));
  const [regions, templates] = options.data ?? [[], [] as FlowTemplate[]];
  const allowed = me.role === 'TENANT_ADMIN' ? regions : regions.filter((r) => r.id === me.region?.id);
  const [value, setValue] = useState({ name: '', regionId: '', template: 'menu-hours-queue-voicemail' });
  const form = useSubmit();
  const navigate = useNavigate();
  const regionId = value.regionId || allowed[0]?.id || '';

  async function submit(event: FormEvent) {
    event.preventDefault();
    const created = await form.submit(newFlowSchema, { ...value, regionId }, (input) => {
      const template = templates.find((x) => x.id === input.template);
      return api.createFlow({ name: input.name, regionId: input.regionId, ...(template && { graph: template.graph }) });
    });
    if (created) navigate(`/flows/${created.id}`);
  }

  if (!options.data) return <div className="dialog-body">{options.error ? <Notice kind="error">{options.error}</Notice> : <Loading />}</div>;
  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body stack">
        <div className="form-grid">
          <Field label={tc('thing', 'Name')} error={form.fields.name}>
            <input value={value.name} onChange={(e) => setValue({ ...value, name: e.target.value })} autoFocus placeholder={t('Main line')} data-testid="flow-new-name" />
          </Field>
          <Field label={t('Region')} hint={t('Its prompts, queues and numbers are the region’s')} error={form.fields.regionId}>
            <select value={regionId} onChange={(e) => setValue({ ...value, regionId: e.target.value })}>
              {allowed.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={t('Start from')} group>
          <div className="template-list">
            {[...templates.map((x) => ({ id: x.id, name: t(x.name), description: t(x.description) })), { id: 'empty', name: t('An empty flow'), description: t('Just the Start.') }].map((option) => (
              <label key={option.id} className={`template-option ${value.template === option.id ? 'on' : ''}`}>
                <input type="radio" name="template" value={option.id} checked={value.template === option.id} onChange={() => setValue({ ...value, template: option.id })} />
                <span>
                  <strong>{option.name}</strong>
                  <span className="subtle">{option.description}</span>
                </span>
              </label>
            ))}
          </div>
        </Field>
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="flow-create">
          {t('Create')}
        </button>
      </div>
    </form>
  );
}
